import { useMemo, useRef, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import InfoPopover from "../../../controls/InfoPopover";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { format } from "date-fns";
import FilterDropdown from "../../../filterDropdown/FilterDropdown";
import FilterSingleSelectDropdown from "../../../filterDropdown/FilterSingleSelectDropdown";
import DateRangeFilter from "../../../filterDropdown/DateRangeFilter";
import Pills, { PillType } from "../../../controls/Pills";
import TableLoadingSkeleton from "../../../shared/TableLoadingSkeleton";
import SlideOver from "../../../slideOver/SlideOver";
import PromotionForm from "./PromotionForm";
import { promotionRuleSummary } from "../../../../shared/promotionFormatting";

const statusOptions = [
  { value: "both", label: "Both" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
const defaultFilters = { status: statusOptions[0], startDate: null, endDate: null };

function statusLabel(promotion) {
  const status = promotion.status || (promotion.is_enabled ? "enabled" : "disabled");
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function matchesPromotionDateRange(promotion, from, to) {
  return (!from || !promotion.ends_on || promotion.ends_on >= from)
    && (!to || !promotion.starts_on || promotion.starts_on <= to);
}

function comparePromotions(first, second) {
  return Number(second.status === "active") - Number(first.status === "active")
    || second.id - first.id;
}

export default function ProductPromotionsTab({
  productSellingUnit,
  promotions,
  customers,
  loading,
  saving,
  error,
  onSave,
  onToggle,
  onDelete,
}) {
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInput = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  const [pendingFilters, setPendingFilters] = useState(defaultFilters);
  const dateFrom = filters.startDate ? format(filters.startDate, "yyyy-MM-dd") : "";
  const dateTo = filters.endDate ? format(filters.endDate, "yyyy-MM-dd") : "";
  const statusFilter = filters.status.value;
  const filteredPromotions = useMemo(() => promotions.filter((promotion) => {
    const active = promotion.status === "active";
    const matchesStatus = statusFilter === "both"
      || (statusFilter === "active" ? active : !active);
    return promotion.name.toLowerCase().includes(searchQuery.toLowerCase())
      && matchesStatus && matchesPromotionDateRange(promotion, dateFrom, dateTo);
  }).sort(comparePromotions), [promotions, statusFilter, dateFrom, dateTo, searchQuery]);
  const nameInput = useRef(null);

  const begin = (promotion = null) => setEditing(promotion || "new");
  const closeForm = () => setEditing(null);

  const promotionBody = (promotion) => (
    <div>
      <div className="font-medium text-gray-900">{promotion.name}</div>
      {promotion.notes ? <div className="mt-1 max-w-sm text-xs text-gray-600">{promotion.notes}</div> : null}
    </div>
  );

  const statusBody = (promotion) => (
    <Pills content={{
      text: statusLabel(promotion),
      type: !promotion.is_enabled ? PillType.Gray
        : promotion.status === "expired" ? PillType.Warning
        : promotion.status === "scheduled" ? PillType.Cyan
        : PillType.Success,
    }} />
  );

  const actionsBody = (promotion) => (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        disabled={saving}
        aria-label={`Edit ${promotion.name}`}
        onClick={() => begin(promotion)}
        className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
      >
        Edit
      </button>
      <button
        type="button"
        disabled={saving}
        aria-label={`${promotion.is_enabled ? "Disable" : "Enable"} ${promotion.name}`}
        onClick={() => onToggle(promotion, !promotion.is_enabled)}
        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
      >
        {promotion.is_enabled ? "Disable" : "Enable"}
      </button>
      <button
        type="button"
        disabled={saving}
        aria-label={`Delete ${promotion.name}`}
        onClick={() => onDelete(promotion)}
        className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50"
      >
        Delete
      </button>
    </div>
  );

  return (
    <section className="space-y-4" aria-label="Product promotions">
      <div className="card-custom bg-white px-4 py-5">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900">Promotions</h2>
          <InfoPopover label="Promotion" title="How promotions work">
            <ul className="list-disc space-y-2 pl-4">
              <li>New shipments save enabled offers matching this product, selling unit and customer. Date ranges include both start and end dates, using the shipment’s original date.</li>
              <li>Quantities combine across lots of the same product/unit. The cheapest qualifying offer applies only if it beats the base price. Offers do not stack.</li>
              <li>The highest qualifying tier prices the entire quantity. Buy 10/get 2 requires 12 scanned units; the discount reduces the average unit price.</li>
              <li>Scans, additions and removals recheck the shipment’s saved offers. Opening a shipment or editing quantity/price does not recheck offers.</li>
              <li>Changing the customer loads current eligible offers and reprices items. Editing, disabling or deleting setup leaves existing shipments unchanged. Historical shipments keep their prices.</li>
              <li>Click the promotion name in the shipment’s Summary to remove it and restore this product’s base prices. Click <strong>Promo removed</strong> to load its latest enabled offers eligible for the shipment’s original date and customer, then reprice it. Other products keep their saved offers.</li>
            </ul>
          </InfoPopover>
        </div>
        <div className="mt-5 flex flex-wrap gap-y-2 rounded-md sm:flex-nowrap">
          <div className="flex min-w-0 flex-grow">
            <div className="relative flex min-w-0 flex-grow items-stretch focus-within:z-10">
              <input
                type="search"
                ref={searchInput}
                aria-label="Search by promotion name"
                placeholder="Search by promotion name"
                className="block w-full rounded-l-md border border-gray-300 pl-2 text-sm"
                onKeyDown={(event) => {
                  if (event.key === "Enter") setSearchQuery(searchInput.current.value.trim());
                }}
              />
            </div>
            <button
              type="button"
              aria-label="Search promotions"
              className="-ml-px relative inline-flex items-center rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1"
              onClick={() => setSearchQuery(searchInput.current.value.trim())}
            >
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </button>
          </div>
          <FilterDropdown
            showFilters={showFilters}
            setShowFilters={(open) => {
              if (open) setPendingFilters(filters);
              setShowFilters(open);
            }}
            actionCall={() => {
              setFilters(pendingFilters);
              setSearchQuery(searchInput.current.value.trim());
            }}
            resetFilters={() => {
              setPendingFilters(defaultFilters);
              setFilters(defaultFilters);
            }}
            filterComponents={[
              <FilterSingleSelectDropdown
                header="Status (today)"
                valueName="promotionStatus"
                value={pendingFilters.status}
                options={statusOptions}
                onChange={(status) => setPendingFilters((current) => ({ ...current, status }))}
                resetFilter={() => setPendingFilters((current) => ({ ...current, status: statusOptions[0] }))}
              />,
              <DateRangeFilter
                startDate={pendingFilters.startDate}
                endDate={pendingFilters.endDate}
                onChange={(name, value) => setPendingFilters((current) => ({ ...current, [name]: value }))}
                resetFilter={() => setPendingFilters((current) => ({ ...current, startDate: null, endDate: null }))}
              />,
              <p className="px-3 py-2 text-xs text-gray-600">Dates match overlapping offers, including permanent promotions.</p>,
            ]}
          />
          <button type="button" disabled={loading || saving} onClick={() => begin()} className="ml-2 whitespace-nowrap rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">Add promotion</button>
        </div>
      </div>

      {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
      <div className="overflow-hidden card-custom bg-white" aria-busy={loading}>
        {loading ? (
          <div role="status" aria-label="Loading promotions">
            <span className="sr-only">Loading promotions</span>
            <TableLoadingSkeleton rows={5} columns={6} widths={["w-1/5", "w-1/6", "w-1/6", "w-1/6", "w-1/12", "flex-1"]} />
          </div>
        ) : (
          <DataTable
            value={filteredPromotions}
            dataKey="id"
            stripedRows
            emptyMessage={promotions.length === 0 ? "No promotions configured for this product." : "No promotions match these filters."}
            tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
            cellClassName="p-2 border-1-bottom-gray text-sm"
          >
            <Column header="Promotion" body={promotionBody} />
            <Column header="Rule" body={promotionRuleSummary} />
            <Column header="Schedule" body={(promotion) => promotion.starts_on || promotion.ends_on ? `${promotion.starts_on || "…"} – ${promotion.ends_on || "…"}` : "Permanent"} />
            <Column header="Customers" body={(promotion) => promotion.applies_to_all_customers ? "All customers" : "Selected customers"} />
            <Column header="Status" body={statusBody} />
            <Column header="Actions" body={actionsBody} />
          </DataTable>
        )}
      </div>

      <SlideOver
        initialFocus={nameInput}
        slideOver={{ open: Boolean(editing) }}
        setSlideOver={closeForm}
        customClass="max-w-2xl"
        childComponent={editing ? (
          <PromotionForm
            key={editing === "new" ? "new" : editing.id}
            promotion={editing === "new" ? null : editing}
            productSellingUnit={productSellingUnit}
            customers={customers}
            saving={saving}
            onSave={onSave}
            onClose={closeForm}
            nameInput={nameInput}
          />
        ) : null}
      />
    </section>
  );
}
